import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';

interface Review {
  id: number;
  name: string;
  email: string;
  role: string | null;
  text: string;
  rating: number;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

type FilterStatus = 'all' | 'pending' | 'approved' | 'rejected';

const ADMIN_PASSWORD = import.meta.env.VITE_ADMIN_PASSWORD || 'Dnlnfl80!!_rec';
const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL || '';
const SUPABASE_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';

const Stars = ({ count }: { count: number }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <i
        key={s}
        className={`${s <= count ? 'ri-star-fill' : 'ri-star-line'} text-amber-400 text-sm`}
      />
    ))}
  </div>
);

const GestioneRecensioni = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterStatus>('pending');
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    if (!authenticated) return;

    // Verifica config prima di fare query
    if (!SUPABASE_URL || !SUPABASE_KEY) {
      setConfigError(
        'Connessione a Supabase non configurata. Le variabili d\'ambiente VITE_PUBLIC_SUPABASE_URL o VITE_PUBLIC_SUPABASE_ANON_KEY sono mancanti.'
      );
      setLoading(false);
      return;
    }

    const fetchReviews = async () => {
      setLoading(true);
      setConnectionError(null);
      setConfigError(null);
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        setConnectionError(
          `Errore di connessione a Supabase: ${error.message}`
        );
        setReviews([]);
      } else if (data) {
        setReviews(data as Review[]);
      }
      setLoading(false);
    };

    fetchReviews();
  }, [authenticated, refreshKey]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      setAuthenticated(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
      setPasswordInput('');
      setTimeout(() => passwordRef.current?.focus(), 50);
    }
  };

  const updateStatus = async (id: number, newStatus: 'approved' | 'rejected') => {
    setUpdatingId(id);
    const { error } = await supabase
      .from('reviews')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (!error) {
      setReviews((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: newStatus } : r))
      );
    }
    setUpdatingId(null);
  };

  const deleteReview = async (id: number) => {
    if (!window.confirm('Sei sicura di voler eliminare definitivamente questa recensione?')) return;
    setUpdatingId(id);
    const { error } = await supabase.from('reviews').delete().eq('id', id);
    if (!error) {
      setReviews((prev) => prev.filter((r) => r.id !== id));
    }
    setUpdatingId(null);
  };

  const filteredReviews =
    activeFilter === 'all'
      ? reviews
      : reviews.filter((r) => r.status === activeFilter);

  const counts = {
    all: reviews.length,
    pending: reviews.filter((r) => r.status === 'pending').length,
    approved: reviews.filter((r) => r.status === 'approved').length,
    rejected: reviews.filter((r) => r.status === 'rejected').length,
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-ivory-50 flex flex-col items-center px-4 pt-20 pb-12">
        <div className="bg-white rounded-xl p-6 sm:p-10 max-w-md w-full border border-terracotta-100">
          <div className="text-center mb-8">
            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-terracotta-100">
              <i className="ri-shield-user-line text-terracotta-500 text-xl" />
            </div>
            <h1 className="font-serif text-2xl text-anthracite-800 font-light">Gestione Recensioni</h1>
            <p className="font-sans text-sm text-anthracite-400 mt-2">Inserisci la password per accedere</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <input
              ref={passwordRef}
              type="password"
              value={passwordInput}
              onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(false); }}
              placeholder="Password"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              className={`w-full font-sans text-base px-4 py-3 rounded-md border bg-white text-anthracite-800 placeholder-anthracite-300 focus:outline-none focus:border-terracotta-300 transition-colors ${passwordError ? 'border-red-400' : 'border-anthracite-200'}`}
            />
            {passwordError && (
              <p className="font-sans text-xs text-red-500">Password errata. Riprova.</p>
            )}
            <button
              type="submit"
              className="w-full font-sans text-sm tracking-widest uppercase px-6 py-3.5 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-all rounded-full cursor-pointer whitespace-nowrap"
            >
              Accedi
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory-50">
      {/* Header */}
      <header className="bg-white border-b border-anthracite-100 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-terracotta-100 shrink-0">
              <i className="ri-shield-user-line text-terracotta-500 text-sm" />
            </div>
            <div className="min-w-0">
              <h1 className="font-serif text-base sm:text-lg text-anthracite-800 font-light truncate">Gestione Recensioni</h1>
              <p className="font-sans text-xs text-anthracite-400 hidden sm:block">Discovering Marche — Pannello Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <button
              onClick={() => setRefreshKey((k) => k + 1)}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-anthracite-200 text-anthracite-500 hover:text-terracotta-300 hover:border-terracotta-300 transition-colors cursor-pointer"
              title="Aggiorna"
            >
              <i className="ri-refresh-line text-sm" />
            </button>
            <button
              onClick={() => setAuthenticated(false)}
              className="font-sans text-xs tracking-widest uppercase px-3 sm:px-4 py-2 border border-anthracite-200 text-anthracite-600 hover:text-terracotta-300 hover:border-terracotta-300 transition-colors rounded-full cursor-pointer whitespace-nowrap"
            >
              Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Errori connessione */}
        {configError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-5 text-left">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-red-100">
                <i className="ri-error-warning-line text-red-500 text-base" />
              </div>
              <div>
                <p className="font-sans text-sm font-medium text-red-700 mb-1">
                  Configurazione Supabase mancante
                </p>
                <p className="font-sans text-sm text-red-600 leading-relaxed">
                  {configError}
                </p>
                <p className="font-sans text-xs text-red-500 mt-3">
                  Se hai ricostruito il sito sull'hosting del cliente, assicurati di copiare anche il file <code>.env</code> prima di lanciare il build. Le variabili VITE_PUBLIC_SUPABASE_URL e VITE_PUBLIC_SUPABASE_ANON_KEY devono essere presenti.
                </p>
              </div>
            </div>
          </div>
        )}

        {connectionError && !configError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-5 text-left">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-red-100">
                <i className="ri-wifi-off-line text-red-500 text-base" />
              </div>
              <div>
                <p className="font-sans text-sm font-medium text-red-700 mb-1">
                  Errore di connessione
                </p>
                <p className="font-sans text-sm text-red-600 leading-relaxed">
                  {connectionError}
                </p>
                <p className="font-sans text-xs text-red-500 mt-3">
                  Possibili cause: il nuovo dominio non è autorizzato nelle impostazioni CORS di Supabase, oppure c'è un blocco firewall sul server di hosting.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6 sm:mb-8">
          {(['all', 'pending', 'approved', 'rejected'] as FilterStatus[]).map((f) => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className={`font-sans text-xs tracking-widest uppercase px-4 py-2.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                activeFilter === f
                  ? 'bg-terracotta-300 text-white border-terracotta-300'
                  : 'bg-white text-anthracite-600 border-anthracite-200 hover:border-terracotta-300 hover:text-terracotta-300'
              }`}
            >
              {f === 'all' && `Tutte (${counts.all})`}
              {f === 'pending' && `In attesa (${counts.pending})`}
              {f === 'approved' && `Approvate (${counts.approved})`}
              {f === 'rejected' && `Rifiutate (${counts.rejected})`}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-10 h-10 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-loader-4-line text-terracotta-300 text-2xl animate-spin" />
            </div>
            <p className="font-sans text-sm text-anthracite-400">Caricamento recensioni...</p>
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-anthracite-100">
            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-ivory-100">
              <i className="ri-inbox-line text-anthracite-300 text-xl" />
            </div>
            <p className="font-sans text-sm text-anthracite-400">
              Nessuna recensione {activeFilter === 'pending' ? 'in attesa' : activeFilter === 'approved' ? 'approvata' : activeFilter === 'rejected' ? 'rifiutata' : ''}.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredReviews.map((review) => (
              <div
                key={review.id}
                className={`bg-white rounded-xl p-4 sm:p-6 border transition-all ${
                  review.status === 'pending'
                    ? 'border-amber-200'
                    : review.status === 'approved'
                      ? 'border-green-200'
                      : 'border-red-200'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-4 sm:gap-5">
                  {/* Left: info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-full bg-terracotta-100 flex items-center justify-center shrink-0">
                        <span className="font-serif text-sm text-terracotta-500 font-medium">
                          {review.name.charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-sans text-sm font-medium text-anthracite-800 truncate">{review.name}</p>
                        <p className="font-sans text-xs text-anthracite-400 truncate">{review.email}</p>
                      </div>
                      <span
                        className={`font-sans text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full ${
                          review.status === 'pending'
                            ? 'bg-amber-50 text-amber-600 border border-amber-200'
                            : review.status === 'approved'
                              ? 'bg-green-50 text-green-600 border border-green-200'
                              : 'bg-red-50 text-red-600 border border-red-200'
                        }`}
                      >
                        {review.status === 'pending' ? 'In attesa' : review.status === 'approved' ? 'Approvata' : 'Rifiutata'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 mb-3">
                      <Stars count={review.rating} />
                      <span className="font-sans text-xs text-anthracite-400">{review.rating}/5</span>
                    </div>

                    {review.role && (
                      <p className="font-sans text-xs text-anthracite-400 mb-2">{review.role}</p>
                    )}

                    <p className="font-sans text-sm text-anthracite-600 leading-relaxed">
                      "{review.text}"
                    </p>

                    <p className="font-sans text-xs text-anthracite-300 mt-3">
                      Ricevuta il {formatDate(review.created_at)}
                    </p>
                  </div>

                  {/* Right: actions */}
                  <div className="flex flex-wrap lg:flex-col gap-2 shrink-0">
                    {review.status === 'pending' && (
                      <>
                        <button
                          onClick={() => updateStatus(review.id, 'approved')}
                          disabled={updatingId === review.id}
                          className="flex items-center justify-center gap-1.5 font-sans text-xs tracking-widest uppercase px-4 py-2.5 bg-green-500 text-white hover:bg-green-600 transition-colors rounded-full cursor-pointer whitespace-nowrap disabled:opacity-50 min-w-[44px] min-h-[44px]"
                        >
                          {updatingId === review.id ? (
                            <i className="ri-loader-4-line animate-spin" />
                          ) : (
                            <i className="ri-check-line" />
                          )}
                          Approva
                        </button>
                        <button
                          onClick={() => updateStatus(review.id, 'rejected')}
                          disabled={updatingId === review.id}
                          className="flex items-center justify-center gap-1.5 font-sans text-xs tracking-widest uppercase px-4 py-2.5 bg-white text-red-500 border border-red-200 hover:bg-red-50 transition-colors rounded-full cursor-pointer whitespace-nowrap disabled:opacity-50 min-w-[44px] min-h-[44px]"
                        >
                          {updatingId === review.id ? (
                            <i className="ri-loader-4-line animate-spin" />
                          ) : (
                            <i className="ri-close-line" />
                          )}
                          Rifiuta
                        </button>
                      </>
                    )}
                    {review.status === 'approved' && (
                      <button
                        onClick={() => updateStatus(review.id, 'rejected')}
                        disabled={updatingId === review.id}
                        className="flex items-center justify-center gap-1.5 font-sans text-xs tracking-widest uppercase px-4 py-2.5 bg-white text-red-500 border border-red-200 hover:bg-red-50 transition-colors rounded-full cursor-pointer whitespace-nowrap disabled:opacity-50 min-w-[44px] min-h-[44px]"
                      >
                        <i className="ri-close-line" />
                        Rifiuta
                      </button>
                    )}
                    {review.status === 'rejected' && (
                      <button
                        onClick={() => updateStatus(review.id, 'approved')}
                        disabled={updatingId === review.id}
                        className="flex items-center justify-center gap-1.5 font-sans text-xs tracking-widest uppercase px-4 py-2.5 bg-green-500 text-white hover:bg-green-600 transition-colors rounded-full cursor-pointer whitespace-nowrap disabled:opacity-50 min-w-[44px] min-h-[44px]"
                      >
                        <i className="ri-check-line" />
                        Approva
                      </button>
                    )}
                    <button
                      onClick={() => deleteReview(review.id)}
                      disabled={updatingId === review.id}
                      className="flex items-center justify-center gap-1.5 font-sans text-xs tracking-widest uppercase px-4 py-2.5 bg-white text-anthracite-400 border border-anthracite-200 hover:text-red-500 hover:border-red-200 transition-colors rounded-full cursor-pointer whitespace-nowrap disabled:opacity-50 min-w-[44px] min-h-[44px]"
                    >
                      <i className="ri-delete-bin-line" />
                      Elimina
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default GestioneRecensioni;